import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthentication } from "../../../Hooks/useAuthentication";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import useStore from "../../../useStore";
import Button from "../../Elements/Button/Button";
import Loader from "../../Elements/Loader/Loader";
import { isAdminRole } from "../../../Constants/roles";

const ChangePassword = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const { role, isAuthenticated } = useAuthentication();
  const {
    fetchDataChangePassword,
    fetchDataGetUsers,
    usersList,
    usersLoading,
    changePasswordLoading,
  } = useStore();

  const [selectedUsername, setSelectedUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);

  const isAdmin = isAdminRole(role);

  useEffect(() => {
    if (isAdmin) fetchDataGetUsers().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">No tienes permisos para acceder a esta sección.</div>
      </div>
    );
  }

  // ✅ solo bloquea la pantalla mientras carga la lista de usuarios;
  // el guardado usa su propio loading en el botón, sin ocultar el formulario.
  if (usersLoading) return <Loader />;

  const newPasswordError = () => {
    if (newPassword && newPassword.length < 8) return "Mínimo 8 caracteres";
    return null;
  };

  const confirmError = () => {
    if (confirmPassword && confirmPassword !== newPassword) return "Las contraseñas no coinciden";
    return null;
  };

  const isValid =
    selectedUsername &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword;

  const handleSubmit = async () => {
    try {
      await fetchDataChangePassword(selectedUsername, newPassword);
      setSuccess(true);
      showToast("success", `Contraseña de '${selectedUsername}' actualizada`);
      setNewPassword("");
      setConfirmPassword("");
      setSelectedUsername("");
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Error al cambiar contraseña");
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-6 col-lg-5">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-1">Cambiar contraseña</h4>
              <p className="text-muted small mb-4">
                Selecciona el usuario y asigna una nueva contraseña.
              </p>

              {success && (
                <div className="alert alert-success">Contraseña actualizada correctamente.</div>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold small">Usuario</label>
                <select
                  className="form-select"
                  value={selectedUsername}
                  onChange={(e) => setSelectedUsername(e.target.value)}
                >
                  <option value="">— Selecciona un usuario —</option>
                  {usersList.map((u) => (
                    <option key={u.userId || u.username} value={u.username}>
                      {u.username} {u.role ? `(${u.role})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small">Nueva contraseña</label>
                <input
                  type="password"
                  className={`form-control ${newPasswordError() ? "is-invalid" : newPassword.length >= 8 ? "is-valid" : ""}`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                />
                {newPasswordError() && (
                  <div className="invalid-feedback">{newPasswordError()}</div>
                )}
              </div>

              <div className="mb-4">
                <label className="form-label fw-bold small">Confirmar contraseña</label>
                <input
                  type="password"
                  className={`form-control ${confirmError() ? "is-invalid" : confirmPassword && !confirmError() ? "is-valid" : ""}`}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la nueva contraseña"
                  autoComplete="new-password"
                />
                {confirmError() && (
                  <div className="invalid-feedback">{confirmError()}</div>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <Button text="Cancelar" color="gray" action={() => navigate("/")} />
                <Button
                  text={changePasswordLoading ? "Guardando..." : "Guardar"}
                  color="green"
                  fill
                  disabled={!isValid || changePasswordLoading}
                  action={handleSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
